import React, { useState, useEffect, useCallback } from 'react';
import {
  getBackupSettings,
  updateBackupSettings,
  listBackups,
  triggerBackup,
  deleteBackup,
} from '../../services/adminApi';
import { notify } from '../../utils/notify';
import './DatabaseBackups.css';

const PAGE_SIZE = 15;
const AUTO_REFRESH_MS = 30_000;

function DatabaseBackups() {
  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  // Backups list state
  const [backups, setBackups] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Trigger / delete
  const [triggering, setTriggering] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, id, filename }

  // ── Load data ──

  const loadSettings = useCallback(async () => {
    try {
      const res = await getBackupSettings();
      const data = res.data;
      setSettings(data);
      setSettingsForm({
        backup_hour: data.backup_hour,
        backup_minute: data.backup_minute,
        retention_days: data.retention_days,
        s3_enabled: data.s3_enabled,
      });
    } catch (err) {
      console.error('Failed to load backup settings:', err);
    }
  }, []);

  const loadBackups = useCallback(async () => {
    try {
      setError(null);
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const res = await listBackups(params);
      const data = res.data;
      setBackups(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load backups:', err);
      setError('Failed to load backup list');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    setLoading(true);
    loadBackups();
  }, [loadBackups]);

  // Auto-refresh when any backup is in_progress or pending
  useEffect(() => {
    const hasRunning = backups.some(
      (b) => b.status === 'in_progress' || b.status === 'pending'
    );
    if (!hasRunning) return;

    const interval = setInterval(loadBackups, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [backups, loadBackups]);

  // ── Handlers ──

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateBackupSettings(settingsForm);
      await loadSettings();
      notify.success('Backup settings saved successfully.');
    } catch (err) {
      console.error('Failed to save settings:', err);
      notify.error('Error saving backup settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTriggerBackup = async () => {
    setTriggering(true);
    setConfirmAction(null);
    try {
      await triggerBackup();
      await loadBackups();
    } catch (err) {
      console.error('Failed to trigger backup:', err);
      notify.error('Error triggering backup.');
    } finally {
      setTriggering(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    setConfirmAction(null);
    try {
      await deleteBackup(backupId);
      await loadBackups();
    } catch (err) {
      console.error('Failed to delete backup:', err);
      notify.error('Error deleting backup.');
    }
  };

  const handleDownload = (backupId) => {
    // Download endpoint is handled by the browser via link
    const token = localStorage.getItem('token');
    window.open(
      `/api/v1/admin/backups/${backupId}/download?token=${token}`,
      '_blank'
    );
  };

  // ── Helpers ──

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const formatTime = (hour, minute) => {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} UTC`;
  };

  // ── Render ──

  return (
    <div className="backups-page">
      <h2>Database Backups</h2>
      <p className="page-subtitle">
        Manage automatic nightly database backups and trigger manual backups.
      </p>

      {/* ── Settings Card ── */}
      {settings && (
        <div className="backups-settings-card">
          <h3>Backup Schedule Settings</h3>
          <div className="settings-form">
            <div className="form-group">
              <label htmlFor="backup-hour">Hour (UTC)</label>
              <input
                id="backup-hour"
                type="number"
                min="0"
                max="23"
                value={settingsForm.backup_hour ?? 1}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    backup_hour: parseInt(e.target.value, 10) || 0,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="backup-minute">Minute</label>
              <input
                id="backup-minute"
                type="number"
                min="0"
                max="59"
                value={settingsForm.backup_minute ?? 0}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    backup_minute: parseInt(e.target.value, 10) || 0,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="retention-days">Retention (days)</label>
              <input
                id="retention-days"
                type="number"
                min="1"
                max="365"
                value={settingsForm.retention_days ?? 14}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    retention_days: parseInt(e.target.value, 10) || 14,
                  }))
                }
              />
            </div>
            <div className="toggle-group">
              <input
                id="s3-enabled"
                type="checkbox"
                checked={settingsForm.s3_enabled ?? true}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    s3_enabled: e.target.checked,
                  }))
                }
              />
              <label htmlFor="s3-enabled">S3 Upload</label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleSaveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
          <p style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
            Current schedule: {formatTime(settings.backup_hour, settings.backup_minute)} daily
          </p>
        </div>
      )}

      {/* ── Actions bar ── */}
      <div className="backups-actions">
        <button
          className="btn btn-success"
          onClick={() =>
            setConfirmAction({ type: 'trigger' })
          }
          disabled={triggering}
        >
          {triggering ? 'Starting...' : 'Run Backup Now'}
        </button>
        <div className="filter-group">
          <label htmlFor="status-filter">Filter by status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="backups-loading">Loading backups...</div>
      ) : error ? (
        <div className="backups-error">{error}</div>
      ) : backups.length === 0 ? (
        <div className="backups-empty">No backups found.</div>
      ) : (
        <>
          <div className="backups-table-wrapper">
            <table className="backups-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Filename</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>S3</th>
                  <th>Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.id}>
                    <td>{b.id}</td>
                    <td>{formatDate(b.created_at)}</td>
                    <td title={b.filename}>
                      {b.filename.length > 40
                        ? b.filename.slice(0, 37) + '...'
                        : b.filename}
                    </td>
                    <td>
                      <span className={`backup-type ${b.backup_type}`}>
                        {b.backup_type}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`backup-status ${b.status}`}
                      >
                        {b.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{formatSize(b.file_size_bytes)}</td>
                    <td>
                      <span
                        className={`s3-badge ${b.s3_uploaded ? 'uploaded' : 'not-uploaded'}`}
                      >
                        {b.s3_uploaded ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      {b.started_at && b.completed_at
                        ? `${Math.round(
                            (new Date(b.completed_at) - new Date(b.started_at)) /
                              1000
                          )}s`
                        : '—'}
                    </td>
                    <td>
                      {b.status === 'completed' && (
                        <>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDownload(b.id)}
                            title="Download backup"
                          >
                            Download
                          </button>{' '}
                        </>
                      )}
                      {b.status !== 'deleted' && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            setConfirmAction({
                              type: 'delete',
                              id: b.id,
                              filename: b.filename,
                            })
                          }
                          title="Delete backup"
                        >
                          Delete
                        </button>
                      )}
                      {b.status === 'failed' && b.error_message && (
                        <span
                          title={b.error_message}
                          style={{ marginLeft: 6, cursor: 'help', color: '#c62828' }}
                        >
                          ⚠
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="backups-pagination">
              <button
                className="btn btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages} ({total} backups)
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Confirm modal ── */}
      {confirmAction && (
        <div
          className="confirm-overlay"
          onClick={() => setConfirmAction(null)}
        >
          <div
            className="confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {confirmAction.type === 'trigger' && (
              <>
                <h4>Run Backup Now?</h4>
                <p>
                  This will trigger a manual database backup immediately. The
                  backup will run in the background.
                </p>
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setConfirmAction(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleTriggerBackup}
                  >
                    Run Backup
                  </button>
                </div>
              </>
            )}
            {confirmAction.type === 'delete' && (
              <>
                <h4>Delete Backup?</h4>
                <p>
                  Are you sure you want to delete the backup{' '}
                  <strong>{confirmAction.filename}</strong>? This will remove
                  both local and S3 copies. This action cannot be undone.
                </p>
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setConfirmAction(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteBackup(confirmAction.id)}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DatabaseBackups;
